/**
 * FavoritenRail – linke Leiste auf der Startseite (pro User, DB-gespeichert).
 *
 * Kacheln der Startseite (Registry-Zeilen mit route) werden per HTML5-Drag
 * hineingezogen (Payload application/x-imho-fav), externe Links über „+ Link".
 * Sortieren per Drag innerhalb der Leiste; jede Änderung = kompletter PUT.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../../api/config";
import { iconByKey } from "../../appRegistry";
import { cacheLesen, cacheSchreiben } from "../../utils/seitenCache";
import "./FavoritenRail.css";

const CACHE_KEY = "home.favoriten";

const api = (path, opts = {}) =>
  fetch(`${API_BASE}/benutzer${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      "X-MA-Token": localStorage.getItem("maToken") || "",
      ...(opts.headers || {}),
    },
  });

export const FAV_MIME = "application/x-imho-fav";

export default function FavoritenRail() {
  const navigate = useNavigate();
  // Zuletzt gesehene Favoriten sofort zeigen, dann im Hintergrund abgleichen
  const [favoriten, setFavoriten] = useState(() => cacheLesen(CACHE_KEY, 24 * 3600) || []);
  const [dragOver, setDragOver] = useState(false);
  const [linkForm, setLinkForm] = useState(null); // {label, url}
  const dragIndex = useRef(null);

  useEffect(() => {
    api("/favoriten").then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) {
          setFavoriten(d.favoriten || []);
          cacheSchreiben(CACHE_KEY, d.favoriten || []);
        }
      })
      .catch(() => {});
  }, []);

  const speichern = useCallback((liste) => {
    setFavoriten(liste);
    cacheSchreiben(CACHE_KEY, liste);
    api("/favoriten", { method: "PUT", body: JSON.stringify({ favoriten: liste }) }).catch(() => {});
  }, []);

  // Drop von außen (Registry-Kachel) oder interne Umsortierung
  const handleDrop = (e, zielIndex = null) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    // interne Umsortierung?
    if (dragIndex.current !== null) {
      const von = dragIndex.current;
      dragIndex.current = null;
      if (zielIndex === null || zielIndex === von) return;
      const kopie = [...favoriten];
      const [item] = kopie.splice(von, 1);
      kopie.splice(zielIndex > von ? zielIndex - 1 : zielIndex, 0, item);
      speichern(kopie);
      return;
    }
    // neue Kachel von außen
    try {
      const data = JSON.parse(e.dataTransfer.getData(FAV_MIME) || "null");
      if (!data || !data.ziel) return;
      if (favoriten.some((f) => f.ziel === data.ziel)) return; // Duplikat
      const neu = { typ: data.typ || "intern", label: data.label || data.ziel,
                    icon: data.icon || "", ziel: data.ziel };
      const kopie = [...favoriten];
      if (zielIndex === null) kopie.push(neu);
      else kopie.splice(zielIndex, 0, neu);
      speichern(kopie);
    } catch (_) {}
  };

  const entfernen = (i) => speichern(favoriten.filter((_, x) => x !== i));

  const oeffnen = (f) => {
    if (f.typ === "extern") window.open(f.ziel, "_blank", "noopener");
    else navigate(f.ziel);
  };

  const linkHinzufuegen = () => {
    const label = (linkForm.label || "").trim();
    let url = (linkForm.url || "").trim();
    if (!label || !url) return;
    if (!/^https?:\/\//i.test(url)) url = "https://" + url;
    speichern([...favoriten, { typ: "extern", label, icon: "", ziel: url }]);
    setLinkForm(null);
  };

  return (
    <aside
      className={`fav-rail ${dragOver ? "dragover" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => handleDrop(e, null)}
    >
      <div className="fav-titel">⭐ Favoriten</div>
      {!favoriten.length && (
        <div className="fav-leer">
          Ziehe Einträge von der Startseite hierher — oder füge unten einen Link hinzu.
        </div>
      )}
      <div className="fav-liste">
        {favoriten.map((f, i) => {
          const Icon = f.typ === "extern" ? iconByKey("FaExternalLinkAlt") : iconByKey(f.icon);
          return (
            <div
              key={`${f.ziel}-${i}`}
              className="fav-item"
              draggable
              onDragStart={() => { dragIndex.current = i; }}
              onDragEnd={() => { dragIndex.current = null; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => handleDrop(e, i)}
              onClick={() => oeffnen(f)}
              title={f.ziel}
            >
              <Icon className="fav-icon" />
              <span className="fav-label">{f.label}</span>
              <button className="fav-x" title="Entfernen"
                onClick={(e) => { e.stopPropagation(); entfernen(i); }}>✕</button>
            </div>
          );
        })}
      </div>
      {linkForm ? (
        <div className="fav-linkform">
          <input className="fav-input" placeholder="Name…" value={linkForm.label} autoFocus
            onChange={(e) => setLinkForm((l) => ({ ...l, label: e.target.value }))} />
          <input className="fav-input" placeholder="https://…" value={linkForm.url}
            onChange={(e) => setLinkForm((l) => ({ ...l, url: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && linkHinzufuegen()} />
          <div className="fav-linkform-buttons">
            <button className="fav-btn" onClick={() => setLinkForm(null)}>Abbrechen</button>
            <button className="fav-btn primary" onClick={linkHinzufuegen}>Hinzufügen</button>
          </div>
        </div>
      ) : (
        <button className="fav-add" onClick={() => setLinkForm({ label: "", url: "" })}>+ Link hinzufügen</button>
      )}
    </aside>
  );
}
